import { getPayments } from "@src/api/payments";
import React, { createContext, useEffect, useState, useMemo, useRef } from "react";
import { isJson, isEmpty } from "@helpers/GlobalFunctions";
import { useAuth } from ".";

const PaymentProvider = ({ children }) => {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const { LoginData } = useAuth();
  const quotaPeriodEndRef = useRef(null);

  const fetchPayment = React.useCallback(() => {
    if (!isJson(LoginData) || isEmpty(LoginData)) {
      setPayment(null);
      setLoading(false);
      quotaPeriodEndRef.current = null;
      return Promise.resolve(null);
    }
    setLoading(true);
    return getPayments()
      .then((res) => {
        const paymentData = res?.data || null;
        setPayment(paymentData);
        const endTs = paymentData?.plan_usage?.quota_period?.period_end_ts;
        quotaPeriodEndRef.current =
          endTs != null && endTs !== "" && !Number.isNaN(Number(endTs)) ? Number(endTs) : null;
        return paymentData;
      })
      .catch((err) => {
        setPayment(null);
        quotaPeriodEndRef.current = null;
        return null;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [LoginData]);

  useEffect(() => {
    fetchPayment();
  }, [fetchPayment]);

  /** Re-fetch plan usage when the tab is reopened or the calendar quota month rolls over. */
  useEffect(() => {
    if (!isJson(LoginData) || isEmpty(LoginData)) return undefined;

    const refreshIfPeriodEnded = () => {
      const endTs = quotaPeriodEndRef.current;
      if (endTs == null) return;
      if (Date.now() / 1000 >= endTs) {
        fetchPayment();
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        refreshIfPeriodEnded();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    const interval = setInterval(refreshIfPeriodEnded, 60_000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
    };
  }, [LoginData, fetchPayment]);

  // Get user's active features based on subscription
  const activeFeatures = useMemo(() => {
    if (!payment || !payment.package) {
      return [];
    }

    // For FIXED packages, get features from package.features (JSONField)
    if (payment.package.package_type === "FIXED") {
      if (payment.package.features && Array.isArray(payment.package.features)) {
        return payment.package.features;
      }
      return [];
    }

    // For PAY_AS_YOU_GO packages, use selected_features
    if (payment.package.package_type === "PAY_AS_YOU_GO" && payment.selected_features) {
      return payment.selected_features.split('\r\n').map((feature) => feature.trim()).filter(f => f);
    }

    return [];
  }, [payment]);

  // Get active jobs limit/count
  const activeJobsInfo = useMemo(() => {
    if (!payment || !payment.package) {
      return { limit: 0, current: 0, remaining: 0 };
    }

    // For FIXED packages, use active_jobs_limit from package
    if (payment.package.package_type === "FIXED") {
      const limit = payment.package.active_jobs_limit || 0;
      const current = payment.active_jobs_count || 0;
      return {
        limit,
        current,
        remaining: Math.max(0, limit - current)
      };
    }

    // For PAY_AS_YOU_GO packages, use active_jobs_count from payment
    if (payment.package.package_type === "PAY_AS_YOU_GO") {
      const limit = payment.active_jobs_count || 0;
      return {
        limit,
        current: limit,
        remaining: 0
      };
    }

    return { limit: 0, current: 0, remaining: 0 };
  }, [payment]);

  // Plan usage from backend (sheets, users, meetings, resumes, chat_users)
  const planUsage = useMemo(() => {
    if (!payment?.plan_usage?.limits || !payment?.plan_usage?.usage) {
      return null;
    }
    const { limits, usage } = payment.plan_usage;
    const info = (limitKey, usedKey) => {
      const limit = limits[limitKey] ?? 0;
      const used = usage[usedKey] ?? 0;
      return { limit, used, remaining: Math.max(0, limit - used) };
    };
    const notesLimit = limits.notes_post_limit;
    const notesUsed = usage.notes_posted ?? 0;
    let notes = null;
    if (notesLimit != null && notesLimit !== "") {
      const lim = Number(notesLimit);
      if (!Number.isNaN(lim)) {
        notes = {
          limit: lim,
          used: notesUsed,
          remaining: Math.max(0, lim - notesUsed),
        };
      }
    }

    const attLimit = limits.note_attachments_per_note_limit;
    let noteAttachmentsPerNote = null;
    if (attLimit != null && attLimit !== "") {
      const lim = Number(attLimit);
      if (!Number.isNaN(lim)) {
        noteAttachmentsPerNote = { limit: lim };
      }
    }

    return {
      sheets: info("sheets_limit", "sheets_used"),
      users: info("users_limit", "users_used"),
      meetings: info("meetings_limit", "meetings_used"),
      resumes: info("resumes_limit", "resumes_used"),
      chatUsers: (() => {
        const limit = (limits.chat_users_limit ?? 0) + (limits.chat_users_addon ?? 0);
        const used = usage.chat_users_used ?? 0;
        return { limit, used, remaining: Math.max(0, limit - used) };
      })(),
      notes,
      noteAttachmentsPerNote,
    };
  }, [payment]);

  const value = useMemo(() => ({
    payment,
    loading,
    activeFeatures,
    activeJobsInfo,
    planUsage,
    hasSubscription: !loading && payment && payment.package,
    refreshPayment: fetchPayment,
  }), [payment, loading, activeFeatures, activeJobsInfo, planUsage, fetchPayment]);

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export const PaymentContext = createContext({});
export default PaymentProvider;
